import React from 'react';
import { formatTime } from '../../utils/date';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AttendanceEventShape } from '../../types/attendance-v2';
import { describeEvent } from '../RunSession';

/**
 * Read-only "today's events for this child" panel, opened from the
 * ChildTile's History action. Reuses the ENDED-view's event-describing logic
 * (`describeEvent`, exported from RunSession.tsx) rather than re-deriving it,
 * filtered down to a single student.
 *
 * Scope note (Task 7): this is read-only. Voiding a mistaken event is called
 * out in the redesign spec as living in History eventually, but building that
 * UI isn't trivial via an existing endpoint from here, so it's deferred —
 * showing the child's history is the acceptable minimum for this task.
 */
const ChildHistoryPanel: React.FC<{
  studentId: string;
  studentName: string;
  events: AttendanceEventShape[];
  onClose: () => void;
}> = ({ studentId, studentName, events, onClose }) => {
  const sorted = events
    .filter((e) => e.studentId === studentId)
    .slice()
    .sort((a, b) => b.at.localeCompare(a.at));

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="child-history-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="child-history-panel-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          data-testid="child-history-panel-backdrop"
          aria-hidden="true"
        />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
          <div className="flex items-start justify-between">
            <h3 id="child-history-panel-title" className="text-lg font-semibold text-gray-900">
              Today · {studentName}
            </h3>
            <button
              type="button"
              onClick={onClose}
              data-testid="child-history-panel-close"
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {sorted.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="child-history-panel-empty">
              No events recorded yet today.
            </p>
          ) : (
            <ul
              className="border-l-2 border-slate-100 ml-2 pl-4 space-y-3"
              data-testid="child-history-panel-list"
            >
              {sorted.map((e) => {
                const voided = Boolean(e.voidedAt);
                return (
                  <li key={e.id} data-testid={`child-history-panel-event-${e.id}`}>
                    <p className="text-xs text-slate-400">{formatTime(e.at)}</p>
                    <p
                      className={
                        voided ? 'text-sm text-slate-400 line-through' : 'text-sm text-slate-900'
                      }
                    >
                      {describeEvent(e)}
                      {voided && (
                        <span className="ml-1 no-underline font-medium text-rose-500">
                          (voided)
                        </span>
                      )}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildHistoryPanel;
